import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { StatusFilter, SubscriptionFilter, AccessRingFilter } from '../types/users.types';

export type UsersTabView = 'users' | 'teams';

const VALID_STATUS_FILTERS = new Set<StatusFilter>(['all', 'active', 'blocked']);
const VALID_SUBSCRIPTION_FILTERS = new Set<SubscriptionFilter>([
  'all',
  'free',
  'basic',
  'pro',
  'media_partner',
  'internal',
]);

function readView(params: URLSearchParams): UsersTabView {
  return params.get('view') === 'teams' ? 'teams' : 'users';
}

function readStatusFilter(params: URLSearchParams): StatusFilter {
  const value = params.get('status');
  return value && VALID_STATUS_FILTERS.has(value as StatusFilter)
    ? value as StatusFilter
    : 'all';
}

function readSubscriptionFilter(params: URLSearchParams): SubscriptionFilter {
  const value = params.get('subscription');
  return value && VALID_SUBSCRIPTION_FILTERS.has(value as SubscriptionFilter)
    ? value as SubscriptionFilter
    : 'all';
}

const VALID_ACCESS_RING_FILTERS = new Set<AccessRingFilter>([
  'all',
  'beta',
  'alpha',
]);

function readAccessRingFilter(params: URLSearchParams): AccessRingFilter {
  const value = params.get('access_ring');
  return value && VALID_ACCESS_RING_FILTERS.has(value as AccessRingFilter)
    ? value as AccessRingFilter
    : 'all';
}

function readPage(params: URLSearchParams): number {
  const raw = params.get('page');
  const parsed = raw ? Number.parseInt(raw, 10) : 1;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function readGuestToggle(params: URLSearchParams): boolean {
  const value = params.get('guest');
  return value === 'true' || value === '1';
}

/**
 * Keeps Manage Clients > Users toolbar filters, view toggle, and drawer selection
 * in sync with the URL for deep links and refresh/share.
 */
export function useUsersTabUrlParams() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setViewState] = useState<UsersTabView>(() => readView(searchParams));
  const [selectedUserId, setSelectedUserIdState] = useState<string | null>(
    () => searchParams.get('user'),
  );
  const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState<string | null>(
    () => searchParams.get('workspace'),
  );
  const [search, setSearchState] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilterState] = useState<StatusFilter>(
    () => readStatusFilter(searchParams),
  );
  const [subscriptionFilter, setSubscriptionFilterState] = useState<SubscriptionFilter>(
    () => readSubscriptionFilter(searchParams),
  );
  const [accessRingFilter, setAccessRingFilterState] = useState<AccessRingFilter>(
    () => readAccessRingFilter(searchParams),
  );
  const [showGuestAccounts, setShowGuestAccountsState] = useState(
    () => readGuestToggle(searchParams),
  );
  const [page, setPageState] = useState(() => readPage(searchParams));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setViewState(readView(params));
    setSelectedUserIdState(params.get('user'));
    setSelectedWorkspaceIdState(params.get('workspace'));
    setSearchState(params.get('search') || '');
    setStatusFilterState(readStatusFilter(params));
    setSubscriptionFilterState(readSubscriptionFilter(params));
    setAccessRingFilterState(readAccessRingFilter(params));
    setShowGuestAccountsState(readGuestToggle(params));
    setPageState(readPage(params));
  }, [location.search]);

  const updateParams = useCallback((
    updater: (params: URLSearchParams) => void,
    options?: { resetPage?: boolean },
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    updater(nextParams);
    if (options?.resetPage) {
      nextParams.delete('page');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const setView = useCallback((next: UsersTabView) => {
    setViewState(next);
    if (next === 'users') {
      setSelectedWorkspaceIdState(null);
    }
    updateParams((params) => {
      if (next === 'teams') {
        params.set('view', 'teams');
      } else {
        params.delete('view');
        params.delete('workspace');
      }
    });
  }, [updateParams]);

  const setSelectedUserId = useCallback((userId: string | null) => {
    setSelectedUserIdState(userId);
    updateParams((params) => {
      if (userId) {
        params.set('user', userId);
      } else {
        params.delete('user');
      }
    });
  }, [updateParams]);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    updateParams((params) => {
      const trimmed = value.trim();
      if (trimmed) {
        params.set('search', trimmed);
      } else {
        params.delete('search');
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setStatusFilter = useCallback((value: StatusFilter) => {
    setStatusFilterState(value);
    updateParams((params) => {
      if (value === 'all') {
        params.delete('status');
      } else {
        params.set('status', value);
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setSubscriptionFilter = useCallback((value: SubscriptionFilter) => {
    setSubscriptionFilterState(value);
    updateParams((params) => {
      if (value === 'all') {
        params.delete('subscription');
      } else {
        params.set('subscription', value);
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setAccessRingFilter = useCallback((value: AccessRingFilter) => {
    setAccessRingFilterState(value);
    updateParams((params) => {
      if (value === 'all') {
        params.delete('access_ring');
      } else {
        params.set('access_ring', value);
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setShowGuestAccounts = useCallback((value: boolean) => {
    setShowGuestAccountsState(value);
    updateParams((params) => {
      if (value) {
        params.set('guest', 'true');
      } else {
        params.delete('guest');
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    setPageState(value);
    updateParams((params) => {
      if (value <= 1) {
        params.delete('page');
      } else {
        params.set('page', String(value));
      }
    });
  }, [updateParams]);

  const setSelectedWorkspaceId = useCallback((workspaceId: string | null) => {
    setSelectedWorkspaceIdState(workspaceId);
    updateParams((params) => {
      if (workspaceId) {
        params.set('workspace', workspaceId);
      } else {
        params.delete('workspace');
      }
    });
  }, [updateParams]);

  const openTeamView = useCallback((workspaceId: string) => {
    setViewState('teams');
    setSelectedWorkspaceIdState(workspaceId);
    setSelectedUserIdState(null);
    updateParams((params) => {
      params.set('view', 'teams');
      params.set('workspace', workspaceId);
      params.delete('user');
    });
  }, [updateParams]);

  return {
    view,
    setView,
    selectedUserId,
    setSelectedUserId,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    subscriptionFilter,
    setSubscriptionFilter,
    accessRingFilter,
    setAccessRingFilter,
    showGuestAccounts,
    setShowGuestAccounts,
    page,
    setPage,
    openTeamView,
  };
}
